import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, BarChart3, CheckCircle2, Filter, Loader2 } from "@digital-twins/design-system/icons";
import type {
  PopulationFilter as SharedPopulationFilter,
  QuickStudyDraftView,
  QuickStudyQuote,
  QuickStudyRunView,
} from "@digital-twins/shared-types";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { CreditIcon } from "@/components/icons/CreditIcon";
import { Progress } from "@/components/ui/progress";
import { AudienceFilterModal } from "@/features/studies/creation/components/AudienceFilterModal";
import { RespondentStepper } from "@/features/studies/creation/components/RespondentStepper";
import { isFilterFullyEditable } from "@/features/studies/creation/components/initialFilterChoices";
import {
  quickStudiesApi,
  type QuickStudyAudienceEdit,
} from "@/lib/api/quick-study-native";
import type { PopulationFilter } from "@/types/routes/test-results";
import {
  quickStudyAudienceFilterText,
  quickStudyFilterErrorKey,
} from "./quickStudyAudienceFilter";
import {
  QUICK_STUDY_MIN_PANEL,
  segmentChipsFromFilter,
  segmentLabelFromChips,
} from "./ThreadRunPane/runSegmentFilter";

/** The card's respondents cap: the Quick Study size cap (`plannedSampleSize` <= 1000). */
const QUICK_STUDY_MAX_RESPONDENTS = 1000;
/** Quiet time after the last size step before the card saves it. */
const SIZE_SAVE_DELAY_MS = 1000;

/** Key order does not change a filter: compare filters by canonical JSON. */
function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${canonicalJson(entry)}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/** The card's own copy for a refusal: on the card the user can lower the twins themselves. */
function cardAudienceErrorKey(
  failure: unknown,
):
  | Exclude<ReturnType<typeof quickStudyFilterErrorKey>, null | "threads.quickStudy.filterTooSmall">
  | "threads.quickStudy.filterTooSmallCard"
  | "threads.quickStudy.audienceSaveFailed" {
  const key = quickStudyFilterErrorKey(failure);
  if (key === "threads.quickStudy.filterTooSmall") {
    return "threads.quickStudy.filterTooSmallCard";
  }
  return key ?? "threads.quickStudy.audienceSaveFailed";
}

/** Run failure codes the engine saves as the run's error, each with its own card line. */
const QUICK_STUDY_VIDEO_UNAVAILABLE = "quick_study_video_unavailable";

/** The card line for a finished run; a failure only says why when its code is known. */
function terminalStatusKey(
  run: QuickStudyRunView,
):
  | "threads.quickStudy.ready"
  | "threads.quickStudy.videoUnavailable"
  | "threads.quickStudy.failed"
  | "threads.quickStudy.cancelled" {
  if (run.status === "completed") {
    return "threads.quickStudy.ready";
  }
  if (run.status === "failed") {
    return run.errorMessage === QUICK_STUDY_VIDEO_UNAVAILABLE
      ? "threads.quickStudy.videoUnavailable"
      : "threads.quickStudy.failed";
  }
  return "threads.quickStudy.cancelled";
}

/** The segment label a filter picked on the card carries: its saved segments, else its chips. */
function segmentLabelForPick(
  filter: PopulationFilter,
  tagNames?: Array<string>,
): string | null {
  if (tagNames && tagNames.length > 0) {
    return tagNames.join(", ");
  }
  const label = segmentLabelFromChips(
    segmentChipsFromFilter(filter as unknown as SharedPopulationFilter),
  );
  return label || null;
}

interface Props {
  threadToken: string;
  draft: QuickStudyDraftView;
  readOnly?: boolean;
  openingResults?: boolean;
  onReview: () => void;
  onRefresh: () => Promise<unknown>;
  onCancelled?: () => void;
}
/** This card never hosts an editor or grows when its saved run finishes. */
export function ThreadQuickStudyCard({
  threadToken,
  draft,
  readOnly: readOnlyProp = false,
  openingResults = false,
  onReview,
  onRefresh,
  onCancelled,
}: Props) {
  const { t } = useTranslation("app");
  const [quote, setQuote] = useState<QuickStudyQuote | null>(null);
  // The last price this card showed. A requote keeps the audience name and
  // twin count on screen; only the credits wait for the new price.
  const [shownQuote, setShownQuote] = useState<QuickStudyQuote | null>(null);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const [accepted, setAccepted] = useState<QuickStudyRunView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingAudience, setSavingAudience] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sizeTarget, setSizeTarget] = useState(
    draft.audience?.plannedSampleSize ?? QUICK_STUDY_MIN_PANEL,
  );
  const inFlight = useRef(false);
  const sizeSaveTimer = useRef<number | null>(null);
  const run = draft.activeRun ?? accepted;
  const audienceFilterText = quickStudyAudienceFilterText(draft.audience);
  const creativesCount = draft.testedAssets?.assets?.length ?? 0;
  // A1: an earlier saved Quick Study a newer one replaced keeps its card as it
  // was, but can no longer be priced, run or cancelled.
  const isReplaced =
    draft.status === "ready" && !draft.activeRun && draft.canEdit === false;
  const readOnly = readOnlyProp || isReplaced;
  const lastQuote = quote ?? shownQuote;
  const eligibleCount = run ? undefined : lastQuote?.eligibleCount;
  const plannedSampleSize = draft.audience?.plannedSampleSize;
  const currentFilter = (draft.audience?.populationFilter ?? null) as
    | PopulationFilter
    | null;
  // The respondents and the filter change only on a priced card nobody ran yet.
  const canEditAudience =
    !readOnly && !run && draft.status === "ready" && Boolean(draft.audience);
  const maxRespondents = Math.min(
    QUICK_STUDY_MAX_RESPONDENTS,
    eligibleCount ?? QUICK_STUDY_MAX_RESPONDENTS,
  );
  // Fewer matching twins than the minimum panel: no size is valid, so none is offered.
  const isSizeLocked = maxRespondents < QUICK_STUDY_MIN_PANEL;
  // A stepped size not yet saved: the quote on screen is for the old size.
  const isSizeDirty = canEditAudience && sizeTarget !== plannedSampleSize;
  const isFilterEditable = isFilterFullyEditable(currentFilter);
  useEffect(() => {
    setAccepted(null);
  }, [draft.draftToken, draft.revision]);
  useEffect(() => {
    if (quote) {
      setShownQuote(quote);
    }
  }, [quote]);
  useEffect(() => {
    setShownQuote(null);
  }, [draft.draftToken]);
  useEffect(() => {
    setSizeTarget(plannedSampleSize ?? QUICK_STUDY_MIN_PANEL);
  }, [draft.draftToken, draft.revision, plannedSampleSize]);
  useEffect(() => () => {
    if (sizeSaveTimer.current !== null) {
      window.clearTimeout(sizeSaveTimer.current);
    }
  }, []);
  useEffect(() => {
    let current = true;
    setQuote(null);
    setError(null);
    if (draft.status !== "ready" || draft.activeRun || readOnly) return;
    void quickStudiesApi
      .quote(threadToken, draft.draftToken, draft.revision)
      .then((value) => {
        if (current) setQuote(value);
      })
      .catch((failure) => {
        if (current)
          setError(
            t(quickStudyFilterErrorKey(failure) ?? "threads.quickStudy.quoteFailed"),
          );
      });
    return () => {
      current = false;
    };
  }, [
    threadToken,
    draft.draftToken,
    draft.revision,
    draft.status,
    draft.activeRun?.runToken,
    readOnly,
    t,
    quoteAttempt,
  ]);
  useEffect(() => {
    if (!quote?.expiresAt) return;
    const remaining = Date.parse(quote.expiresAt) - Date.now();
    if (!Number.isFinite(remaining)) {
      setQuote(null);
      setError(t("threads.quickStudy.quoteFailed"));
      return;
    }
    const expiry = setTimeout(
      () => {
        setQuote(null);
        setError(t("threads.quickStudy.quoteFailed"));
      },
      Math.max(0, remaining),
    );
    return () => clearTimeout(expiry);
  }, [quote, t]);
  async function approve() {
    if (!quote || inFlight.current || run || readOnly) return;
    if (quote.expiresAt && Date.parse(quote.expiresAt) <= Date.now()) {
      setQuote(null);
      setError(t("threads.quickStudy.quoteFailed"));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const next = await quickStudiesApi.accept(
        threadToken,
        draft.draftToken,
        quote,
      );
      setAccepted(next);
      await onRefresh();
    } catch (failure) {
      const code = (failure as { response?: { data?: { code?: string } } })
        ?.response?.data?.code;
      if (
        code === "quick_study_quote_expired" ||
        code === "quick_study_quote_stale"
      )
        setQuote(null);
      setError(t("threads.quickStudy.launchFailed"));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function saveAudience(edit: QuickStudyAudienceEdit) {
    if (inFlight.current || !canEditAudience || !draft.audience) return;
    inFlight.current = true;
    setSavingAudience(true);
    setAudienceError(null);
    try {
      try {
        await quickStudiesApi.saveAudience(
          threadToken,
          draft.draftToken,
          draft.revision,
          edit,
        );
      } catch (failure) {
        setSizeTarget(plannedSampleSize ?? QUICK_STUDY_MIN_PANEL);
        setAudienceError(t(cardAudienceErrorKey(failure)));
        return;
      }
      // The new revision comes back through the refresh and requotes the card.
      // The save already landed: a failed refresh keeps the new value on screen.
      try {
        await onRefresh();
      } catch {
        // Nothing to undo: the next refresh shows the saved revision.
      }
    } finally {
      inFlight.current = false;
      setSavingAudience(false);
    }
  }
  // Steps land on one save: each click restarts a short wait, so ten clicks
  // write one revision and requote once.
  function changeSize(next: number) {
    setSizeTarget(next);
    if (sizeSaveTimer.current !== null) {
      window.clearTimeout(sizeSaveTimer.current);
    }
    if (next === plannedSampleSize) {
      sizeSaveTimer.current = null;
      return;
    }
    sizeSaveTimer.current = window.setTimeout(() => {
      sizeSaveTimer.current = null;
      void saveAudience({
        plannedSampleSize: next,
        populationFilter: draft.audience?.populationFilter ?? null,
        segmentLabel: draft.audience?.segmentLabel ?? null,
      });
    }, SIZE_SAVE_DELAY_MS);
  }
  function applyFilter(filter: PopulationFilter, _count: number, tagNames?: Array<string>) {
    setFilterOpen(false);
    if (plannedSampleSize === undefined) return;
    const segmentLabel = segmentLabelForPick(filter, tagNames);
    // The same filter back is no edit. Its chip label adds nothing; only a
    // saved-segment name the label does not carry yet is worth a revision.
    const isUnchanged =
      canonicalJson(filter) === canonicalJson(currentFilter) &&
      (!tagNames?.length || segmentLabel === (draft.audience?.segmentLabel ?? null));
    if (isUnchanged) return;
    void saveAudience({
      plannedSampleSize,
      populationFilter: filter as unknown as Record<string, unknown>,
      segmentLabel,
    });
  }
  function clearFilter() {
    setFilterOpen(false);
    if (plannedSampleSize === undefined || !currentFilter?.conditions?.length) return;
    void saveAudience({
      plannedSampleSize,
      populationFilter: null,
      segmentLabel: null,
    });
  }
  async function cancel() {
    if (inFlight.current || readOnly) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await quickStudiesApi.cancelDraft(
        threadToken,
        draft.draftToken,
      );
      if (result.kind === "run_cancellation_required")
        await quickStudiesApi.cancelRun(
          threadToken,
          draft.draftToken,
          result.runToken,
        );
      await onRefresh();
      onCancelled?.();
    } catch {
      setError(t("threads.quickStudy.cancelFailed"));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  const running = run?.status === "pending" || run?.status === "running";
  const terminal = Boolean(run && !running);
  const answering = running && run?.progressPhase === "answering";
  const activeStatusCopy = run?.status === "pending" || run?.progressPhase === "preparing"
    ? "threads.sessions.preparing"
    : run?.progressPhase === "analyzing"
      ? "threads.sessions.analysing"
      : answering
        ? "studyReport.collecting"
        : "threads.quickStudy.running";

  const progress =
    run?.progressTotal &&
    run.progressTotal > 0 &&
    run.progressCompleted !== null
      ? Math.max(
          0,
          Math.min(100, (run.progressCompleted / run.progressTotal) * 100),
        )
      : null;
  // Preparation remains conversational until a real instrument is saved.
  // A1: preparation still talks in the chat, but the card shows it is waiting.
  if (!draft.instrument)
    return (
      <section
        className="flex flex-col overflow-hidden rounded-2xl border border-border bg-background text-foreground"
        data-testid="thread-native-quick-study-needs-input-card"
        data-draft-token={draft.draftToken}
      >
        <header className="flex items-center gap-3 border-b border-border px-4 py-3">
          <BarChart3 className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <Text size="sm" className="min-w-0 flex-1 truncate">
            {draft.title}
          </Text>
          <Text size="xs" color="muted" className="shrink-0">
            {t("threads.quickStudy.title")}
          </Text>
        </header>
        <div className="flex flex-col gap-1 px-4 py-3" aria-live="polite">
          {error ? (
            <Text size="sm" className="text-destructive" role="alert">
              {error}
            </Text>
          ) : null}
          <Text size="sm">
            {t(
              draft.status === "cancelled"
                ? "threads.quickStudy.cancelled"
                : "threads.quickStudy.waitingForInput",
            )}
          </Text>
          {draft.status === "cancelled" ? null : (
            <Text size="xs" color="muted">
              {t("threads.quickStudy.needsInputHint")}
            </Text>
          )}
        </div>
        {!readOnly && draft.status !== "cancelled" ? (
          <footer className="flex items-center border-t border-border px-4 py-2">
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void cancel()}
              data-testid="thread-native-quick-study-needs-input-cancel"
            >
              {t("cancel", { ns: "common" })}
            </Button>
          </footer>
        ) : null}
      </section>
    );
  return (
    <section
      className="flex h-64 flex-col overflow-hidden rounded-2xl border border-primary/20 bg-background text-foreground"
      data-testid="thread-native-quick-study-card"
      data-draft-token={draft.draftToken}
      aria-busy={running}
    >
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
        <BarChart3 className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <Text size="sm" className="min-w-0 flex-1 truncate">
          {draft.title}
        </Text>
        <Text size="xs" color="muted" className="shrink-0">
          {t("threads.quickStudy.title")}
        </Text>
      </header>
      <div
        className="flex min-h-0 flex-1 flex-col justify-center gap-2 px-4 py-3"
        aria-live="polite"
      >
        {error ? (
          <Text size="sm" className="text-destructive" role="alert">
            {error}
          </Text>
        ) : run ? (
          <>
            <Text
              size="sm"
              color={run.status === "completed" ? "muted" : undefined}
              className="flex shrink-0 items-center gap-2"
              data-testid="quick-study-run-status"
            >
              {running ? <Loader2 className="h-4 w-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden data-testid="quick-study-running-indicator" /> : null}
              {run.status === "completed" ? <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden /> : null}
              {t(running ? activeStatusCopy : terminalStatusKey(run))}
            </Text>
            {answering && progress !== null ? (
              <Progress value={progress} className="h-1.5" />
            ) : null}
            {answering &&
            run.progressTotal !== null &&
            run.progressCompleted !== null ? (
              <Text size="xs" color="muted">
                {t("studyReport.collectedOf", {
                  completed: run.progressCompleted,
                  total: run.progressTotal,
                })}
              </Text>
            ) : null}
          </>
        ) : draft.status === "ready" ? (
          <>
            {isReplaced ? (
              <Text size="sm" color="muted" data-testid="quick-study-replaced">
                {t("threads.quickStudy.replaced")}
              </Text>
            ) : null}
            {canEditAudience ? (
              <div className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1">
                <Text size="xs" color="muted" className="flex items-center gap-1.5">
                  {lastQuote ? <span>{lastQuote.audienceLabel} ·</span> : null}
                  <RespondentStepper
                    compact
                    id={`quick-study-respondents-${draft.draftToken}`}
                    label={t("threads.quickStudy.respondents")}
                    value={sizeTarget}
                    limits={{ min: QUICK_STUDY_MIN_PANEL, max: Math.max(QUICK_STUDY_MIN_PANEL, maxRespondents) }}
                    onChange={changeSize}
                    disabled={busy || savingAudience || isSizeLocked}
                    data-testid="quick-study-respondents-input"
                  />
                  <span>
                    {t("threads.quickStudy.respondentsUnit", {
                      count: sizeTarget,
                    })}
                  </span>
                </Text>
                <span
                  title={isFilterEditable ? undefined : t("threads.quickStudy.filterTooComplex")}
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                    disabled={busy || savingAudience || !isFilterEditable}
                    aria-label={
                      isFilterEditable
                        ? undefined
                        : `${t("threads.quickStudy.editFilter")}. ${t("threads.quickStudy.filterTooComplex")}`
                    }
                    onClick={() => setFilterOpen(true)}
                    data-testid="quick-study-edit-filter"
                  >
                    <Filter className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {t("threads.quickStudy.editFilter")}
                  </button>
                </span>
              </div>
            ) : quote ? (
              <Text size="xs" color="muted">
                {quote.audienceLabel} ·{" "}
                {t("threads.quickStudy.twins", { count: quote.numRespondents })}
              </Text>
            ) : null}
            {audienceError ? (
              <Text
                size="xs"
                className="text-destructive"
                role="alert"
                data-testid="quick-study-audience-error"
              >
                {audienceError}
              </Text>
            ) : null}
          </>
        ) : (
          <Text size="sm" color="muted">
            {t(
              draft.status === "cancelled"
                ? "threads.quickStudy.cancelled"
                : "threads.quickStudy.waitingForInput",
            )}
          </Text>
        )}
        {audienceFilterText ? (
          <Text
            size="xs"
            color="muted"
            className="shrink-0"
            data-testid="quick-study-audience-filter"
          >
            {t("threads.quickStudy.filteredTo", { filter: audienceFilterText })}
            {eligibleCount !== undefined ? (
              <>
                {" \u00b7 "}
                <span data-testid="quick-study-eligible-count">
                  {t("threads.quickStudy.eligible", { count: eligibleCount })}
                </span>
              </>
            ) : null}
          </Text>
        ) : null}
        {creativesCount > 0 ? (
          <Text
            size="xs"
            color="muted"
            className="shrink-0"
            data-testid="quick-study-creatives-attached"
          >
            {t("threads.quickStudy.creativesAttached", { count: creativesCount })}
          </Text>
        ) : null}
        {draft.instrument?.questions.length ? (
          <ol className="min-h-0 list-decimal space-y-2 overflow-y-auto pl-5 text-sm">
            {draft.instrument.questions.map((question) => (
              <li key={question.id}>
                <span className="line-clamp-2">{question.title}</span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
      <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-4 py-3">
        {!readOnly && !terminal && draft.status !== "cancelled" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => void cancel()}
          >
            {t("cancel", { ns: "common" })}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          {draft.instrument ? (
            <Button
              size="sm"
              variant={run?.status === "completed" ? "primary" : "outline"}
              disabled={openingResults}
              onClick={onReview}
            >
              {t(
                run?.status === "completed"
                  ? "threads.quickStudy.openResults"
                  : "threads.quickStudy.reviewQuestions",
              )}
              {openingResults ? <Loader2 className="ml-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden /> : null}
              {run?.status === "completed" && !openingResults ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden /> : null}
            </Button>
          ) : null}
          {!run && draft.status === "ready" && !readOnly && !quote && error ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => setQuoteAttempt((value) => value + 1)}
            >
              {t("threads.quickStudy.retry")}
            </Button>
          ) : null}
          {!run && draft.status === "ready" && !readOnly ? (
            <Button
              size="sm"
              disabled={!quote || busy || savingAudience || isSizeDirty}
              onClick={() => void approve()}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <>
                  {t("threads.quickStudy.approve")}
                  <span className="ml-3 inline-flex items-center gap-1.5 border-l border-primary-foreground/20 pl-3">
                    <CreditIcon className="h-4 w-4" />
                    {/* Only the credits wait for a new price; the button keeps its size. */}
                    {quote && !savingAudience && !isSizeDirty ? (
                      <span className="tabular-nums" data-testid="quick-study-credits">
                        {quote.credits}
                      </span>
                    ) : (
                      <Loader2
                        className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                        aria-label={t("threads.quickStudy.pricing")}
                        data-testid="quick-study-credits-pending"
                      />
                    )}
                  </span>
                </>
              )}
            </Button>
          ) : null}
        </div>
      </footer>
      {canEditAudience && filterOpen && draft.audience ? (
        <AudienceFilterModal
          open={filterOpen}
          onOpenChange={setFilterOpen}
          populationId={draft.audience.audienceToken}
          populationName={lastQuote?.audienceLabel ?? ""}
          coverImageUrl={null}
          prefetchedData={null}
          onFilterApply={applyFilter}
          onContinueWithout={clearFilter}
          minPersonas={QUICK_STUDY_MIN_PANEL}
          forStudy
          initialFilter={currentFilter}
        />
      ) : null}
    </section>
  );
}
